import React, { useEffect, useState, createContext, useContext } from "react";
import { Outlet, Navigate, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import MobileTabs from "./MobileTabs";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

const SiteContext = createContext(null);
export const useSiteContext = () => useContext(SiteContext);

export default function Layout() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allSites, setAllSites] = useState([]);
  const [sites, setSites] = useState([]);
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [selectedSite, setSelectedSite] = useState(searchParams.get("siteId") || "");
  const [alerts, setAlerts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(searchParams.get("orgId") || "");
  const [siteConfigVersion, setSiteConfigVersion] = useState(0);
  const bumpSiteConfig = () => setSiteConfigVersion((v) => v + 1);
  const [prevOrg, setPrevOrg] = useState(searchParams.get("orgId") || "");

  const isPlatform = user?.role === "super_admin" || user?.role === "platform_support";

  useEffect(() => {
    if (!user) return;
    api.get("/sites").then((r) => {
      setAllSites(r.data);
      setSites(r.data);
      // Only set initial site if not already set (avoid overwriting user selection)
      if (!selectedSite && r.data.length) {
        const urlSiteId = searchParams.get("siteId");
        const siteToUse = urlSiteId && r.data.some((s) => s.site_id === urlSiteId) ? urlSiteId : r.data[0].site_id;
        setSelectedSite(siteToUse);
      }
      setSitesLoaded(true);
    });
  }, [user]);

  useEffect(() => {
    if (!isPlatform) return;
    api.get("/admin/orgs").then((r) => {
      setOrgs(r.data);
      // Pre-select first org so the site panel is never empty on first load
      if (r.data.length && !selectedOrg) setSelectedOrg(r.data[0].org_id);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!isPlatform) return;
    if (selectedOrg) {
      const filtered = allSites.filter((s) => s.org_id === selectedOrg);
      setSites(filtered);
      // Only reset site if current selected site is not in the filtered list
      if (!filtered.some((s) => s.site_id === selectedSite)) {
        setSelectedSite(filtered.length ? filtered[0].site_id : "");
      }
    } else {
      setSites(allSites);
      // Only reset site if current selected site is not in allSites
      if (!allSites.some((s) => s.site_id === selectedSite)) {
        setSelectedSite(allSites.length ? allSites[0].site_id : "");
      }
    }
  }, [selectedOrg, allSites, selectedSite]);

  useEffect(() => {
    if (!isPlatform || !selectedOrg) return;
    // Redirect to dashboard when tenant changes
    if (prevOrg && prevOrg !== selectedOrg && loc.pathname !== "/dashboard") {
      nav("/dashboard");
    }
    setPrevOrg(selectedOrg);
  }, [selectedOrg, prevOrg, isPlatform, loc.pathname, nav]);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/alerts", { params: { limit: 50 } }).then((r) => setAlerts(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedOrg) params.set("orgId", selectedOrg);
    if (selectedSite) params.set("siteId", selectedSite);
    setSearchParams(params, { replace: true });
  }, [selectedSite, selectedOrg, setSearchParams]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  // First-login redirect: new users with no site → wizard (unless already there)
  if (user.wizard_completed === false && loc.pathname !== "/first-run") {
    return <Navigate to="/first-run" replace />;
  }

  const unackCount = alerts.filter((a) => !a.acknowledged).length;
  const selectedSiteObj = sites.find((s) => s.site_id === selectedSite) || null;

  return (
    <SiteContext.Provider value={{ sites, setSites, sitesLoaded, selectedSite, selectedSiteObj, setSelectedSite, alerts, setAlerts, orgs, selectedOrg, setSelectedOrg, siteConfigVersion, bumpSiteConfig }}>
      <div className="min-h-screen bg-gray-50 text-gray-900">
        <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
        <div className={`transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-20'}`}>
          <Header selectedSite={selectedSite} setSelectedSite={setSelectedSite} alertsCount={unackCount} alerts={alerts} />
          <main className="flex-1 p-4 md:p-6 pb-20 md:pb-6 overflow-x-hidden">
            <Outlet />
          </main>
        </div>
        <MobileTabs />
      </div>
    </SiteContext.Provider>
  );
}
